// LINKS page (/links). Owner rule (2026-09-10): "every table has Add and
// Edit." Was a hard-coded array (client/src/lib/managerLogins.ts —
// ME_LINKS, OUR_LINKS); now a real table (login_link, 077_login_link.sql),
// read by every signed-in role and writable by OWNER/DIRECTOR only
// (src/login_links.js). Same add/edit/soft-delete pattern as
// CompanyLicenses.tsx, minus clinic scoping — a login link is company-wide.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { FaultState } from '../components/FaultState';
import { LoginMark, hasLoginMark } from '../components/LoginMark';
import { useWrite } from '../components/MoneyForms';
import { useResource } from '../lib/store';
import { session } from '../lib/session';
import {
  LOGIN_LINKS_KEY, fetchLoginLinks, addLoginLink, updateLoginLink, deleteLoginLink,
  reloadLoginLinks,
  type LoginLinkRow, type LoginLinksData, type LoginLinkGroup, type LoginLinkSize,
} from '../lib/loginLinks';

function canWriteLoginLinks(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

const SECTION_TITLES: Record<LoginLinkGroup, string> = {
  me: 'ME links',
  our: 'Our Links',
};

const SECTION_ORDER: LoginLinkGroup[] = ['me', 'our'];

interface FormState {
  id: number;
  label: string;
  href: string;
  group: LoginLinkGroup;
  size: LoginLinkSize;
  sortOrder: string;
}

function blank(): FormState {
  return {
    id: 0, label: '', href: '', group: 'me', size: 'md', sortOrder: '0',
  };
}

export function LoginsPage(): JSX.Element {
  const data = useResource<LoginLinksData>(LOGIN_LINKS_KEY, fetchLoginLinks);
  const save = useWrite();
  const [form, setForm] = useState<FormState>(blank);
  const [formOpen, setFormOpen] = useState(false);
  const canWrite = canWriteLoginLinks();

  function patch(next: Partial<FormState>): void {
    setForm((prev) => ({ ...prev, ...next }));
  }

  function startAdd(): void {
    setForm(blank());
    setFormOpen(true);
  }

  function startEdit(row: LoginLinkRow): void {
    setForm({
      id: row.id,
      label: row.label,
      href: row.href,
      group: row.group,
      size: row.size,
      sortOrder: String(row.sortOrder),
    });
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
    setForm(blank());
  }

  async function submit(): Promise<void> {
    await save.run(async () => {
      const body = {
        label: form.label.trim(),
        href: form.href.trim(),
        group: form.group,
        size: form.size,
        sortOrder: Number.isFinite(Number(form.sortOrder)) ? Math.trunc(Number(form.sortOrder)) : 0,
      };
      if (form.id) await updateLoginLink(form.id, body);
      else await addLoginLink(body);
      closeForm();
      await reloadLoginLinks();
    });
  }

  async function remove(row: LoginLinkRow): Promise<void> {
    await save.run(async () => {
      await deleteLoginLink(row.id);
      if (form.id === row.id) closeForm();
      await reloadLoginLinks();
    });
  }

  // Loading shows nothing but the header — never the old hard-coded tiles,
  // which do not exist here to fall back to any more.
  if (data.status === 'loading') {
    return (
      <div class="main">
        <div class="page-header">
          <h1>LINKS</h1>
        </div>
      </div>
    );
  }
  if (data.status === 'fault') return <FaultState name="login_links_load_failed" detail={data.error} />;

  const rows = data.data.rows ?? [];
  const sections = SECTION_ORDER.map((id) => ({
    id,
    title: SECTION_TITLES[id],
    tiles: rows.filter((r) => r.group === id),
  }));

  return (
    <div class="main">
      <div class="page-header">
        <div class="section-head-row">
          <h1>LINKS</h1>
          {canWrite ? (
            <div class="section-head-actions">
              <button type="button" class="btn" data-testid="add-login-link" onClick={startAdd}>
                Add link
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <p class="page-note">Tools managers sign into. Opens in a new tab.</p>

      {canWrite && formOpen ? (
        <form
          class="service-point-form card"
          data-testid="login-link-form"
          onSubmit={(e) => { e.preventDefault(); void submit(); }}
        >
          <label>
            Label
            <input
              required
              value={form.label}
              onInput={(e) => patch({ label: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            URL
            <input
              required
              placeholder="https://"
              value={form.href}
              onInput={(e) => patch({ href: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label>
            Group
            <select
              aria-label="Group"
              value={form.group}
              onChange={(e) => patch({ group: (e.target as HTMLSelectElement).value as LoginLinkGroup })}
            >
              <option value="me">ME links</option>
              <option value="our">Our Links</option>
            </select>
          </label>
          <label>
            Size
            <select
              aria-label="Size"
              value={form.size}
              onChange={(e) => patch({ size: (e.target as HTMLSelectElement).value as LoginLinkSize })}
            >
              <option value="lg">Large</option>
              <option value="md">Medium</option>
              <option value="sm">Small</option>
            </select>
          </label>
          <label>
            Order
            <input
              type="number"
              value={form.sortOrder}
              onInput={(e) => patch({ sortOrder: (e.target as HTMLInputElement).value })}
            />
          </label>
          <div class="service-point-actions">
            <button
              type="submit"
              class="btn primary"
              disabled={save.busy || !form.label.trim() || !form.href.trim()}
            >
              {form.id ? 'Save' : 'Add'}
            </button>
            <button type="button" class="btn" disabled={save.busy} onClick={closeForm}>
              Cancel
            </button>
          </div>
          {save.error ? <p class="bad" role="alert">{save.error}</p> : null}
        </form>
      ) : null}

      <div class="login-board">
        {sections.map((section) => (
          <section key={section.id} class="login-section" data-testid={`login-section-${section.id}`}>
            <div class="section-head">
              <h2>{section.title}</h2>
            </div>
            <div class="login-mosaic">
              {section.tiles.map((tile) => (
                <div class="login-tile-cell" key={tile.id}>
                  <a
                    class={`login-tile${hasLoginMark(tile.label) ? '' : ' wordmark'}`}
                    href={tile.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`login-tile-${tile.id}`}
                  >
                    <span class="login-tile-mark">
                      {hasLoginMark(tile.label)
                        ? <LoginMark label={tile.label} />
                        : <span class="login-word">{tile.label}</span>}
                    </span>
                    {hasLoginMark(tile.label) ? <span class="login-tile-label">{tile.label}</span> : null}
                  </a>
                  {canWrite ? (
                    <div class="login-tile-tools">
                      <button
                        type="button"
                        class="btn"
                        data-testid={`login-edit-${tile.id}`}
                        disabled={save.busy}
                        onClick={() => startEdit(tile)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        class="btn"
                        data-testid={`login-remove-${tile.id}`}
                        disabled={save.busy}
                        onClick={() => { void remove(tile); }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
